import { AnimatePresence, motion } from 'framer-motion';
import { Check, Loader2, X, Zap } from 'lucide-react';
import React, { useState } from 'react';

import { cn } from '@/lib/utils';
import { RainmakerSuggestion } from '@/services/types/rainmaker-types';

import { RainmakerLogo } from './RainmakerLogo';

interface ExecutionModalProps {
  suggestion: RainmakerSuggestion;
  onClose: () => void;
  onExecute: () => Promise<void>;
  isExecuting: boolean;
}

export const ExecutionModal: React.FC<ExecutionModalProps> = ({
  suggestion,
  onClose,
  onExecute,
  isExecuting,
}) => {
  const [executedActions, setExecutedActions] = useState<Set<number>>(
    new Set(),
  );
  const [currentExecutingIndex, setCurrentExecutingIndex] = useState<
    number | null
  >(null);

  const handleExecute = async () => {
    // Simulate sequential execution with animations
    for (let i = 0; i < suggestion.actions.length; i += 1) {
      setCurrentExecutingIndex(i);
      // Small delay to show the animation
      // eslint-disable-next-line no-await-in-loop
      await new Promise<void>((resolve) => {
        setTimeout(() => resolve(), 400);
      });
      setExecutedActions((prev) => new Set(prev).add(i));
    }
    setCurrentExecutingIndex(null);

    // Actually execute
    await onExecute();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/30 p-4 backdrop-blur-sm sm:p-6">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.2 }}
        className="relative flex  w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
      >
        {/* Modal Header: Rainmaker Branding */}
        <div className="relative h-14 shrink-0 overflow-hidden bg-[#0B0F19] px-4">
          {/* Animated background */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-violet-900/40 via-[#0B0F19] to-[#000000]" />
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-violet-500/10 to-transparent"
            animate={{ x: ['-100%', '100%'] }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'linear',
            }}
          />
          <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-violet-500/30" />

          <div className="relative z-10 flex h-full items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg border border-violet-500/40 bg-violet-500/20">
                <RainmakerLogo size={14} className="text-violet-300" />
              </div>
              <div>
                <div className="text-[10px] font-black uppercase tracking-[0.2em] text-violet-200">
                  Rainmaker Execution
                </div>
                <h2 className="text-xs font-semibold tracking-tight text-white">
                  {suggestion.title}
                </h2>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              disabled={isExecuting}
              className="rounded-lg bg-white/5 p-1.5 text-slate-400 transition-colors hover:bg-white/10 hover:text-white disabled:opacity-50"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-4 pt-2">
          {/* Description */}
          {suggestion.description && (
            <div className="mb-4">
              <p className="text-xs leading-relaxed text-slate-800 font-medium">
                {suggestion.description}
              </p>
            </div>
          )}

          <div className="mb-3">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              The system will automatically execute these actions:
            </p>
          </div>

          {/* Vertical Timeline */}
          <div className="relative space-y-4 border-l-2 border-slate-200 pl-4">
            <AnimatePresence>
              {suggestion.actions.map((action, index) => {
                const isExecuted = executedActions.has(index);
                const isExecutingAction = currentExecutingIndex === index;

                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="relative"
                  >
                    {/* Timeline Node */}
                    <div
                      className={cn(
                        'absolute -left-[23px] top-0 h-3 w-3 rounded-full border-2 transition-colors',
                        isExecuted && 'border-green-500 bg-green-500',
                        isExecutingAction && 'border-violet-500 bg-violet-500',
                        !isExecuted &&
                          !isExecutingAction &&
                          'border-slate-300 bg-white',
                      )}
                    >
                      {isExecutingAction && (
                        <motion.div
                          className="absolute inset-0 -m-1 rounded-full border-2 border-violet-300"
                          animate={{
                            scale: [1, 1.5, 1],
                            opacity: [1, 0, 1],
                          }}
                          transition={{
                            duration: 1.5,
                            repeat: Infinity,
                            ease: 'easeInOut',
                          }}
                        />
                      )}
                    </div>

                    {/* Step Content */}
                    <div className="flex flex-col">
                      <div className="mb-0.5 flex items-center gap-2">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                          STEP {String(index + 1).padStart(2, '0')}
                        </span>
                        {isExecutingAction && (
                          <span className="flex items-center gap-1 text-[10px] font-medium text-violet-600">
                            <Loader2 className="h-2.5 w-2.5 animate-spin" />
                            Executing
                          </span>
                        )}
                        {isExecuted && !isExecutingAction && (
                          <motion.span
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            className="flex items-center gap-1 text-[10px] font-medium text-green-600"
                          >
                            <Check className="h-2.5 w-2.5" />
                            Complete
                          </motion.span>
                        )}
                      </div>
                      <h4 className="text-xs font-semibold text-slate-900">
                        {action.description}
                      </h4>
                      <p className="mt-0.5 text-[10px] text-slate-500">
                        Type: {action.type}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>

          {suggestion.actions.length === 0 && (
            <div className="py-6 text-center text-xs text-slate-500">
              No actions defined
            </div>
          )}
        </div>

        {/* Footer CTA */}
        <div className="border-t border-slate-200 bg-slate-50 p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="text-[10px] text-slate-600">
              <strong className="font-semibold text-slate-900">
                {suggestion.actions?.length || 0} actions
              </strong>{' '}
              will be executed automatically by the system.
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={isExecuting}
                className="rounded-lg px-3 py-1.5 text-[10px] font-medium text-slate-600 transition-colors hover:bg-slate-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleExecute}
                disabled={isExecuting || suggestion.actions.length === 0}
                className={cn(
                  'group flex items-center gap-1.5 rounded-lg bg-slate-900 px-4 py-1.5 text-[10px] font-bold text-white shadow-lg shadow-violet-900/10 transition-all hover:scale-[1.02] hover:bg-violet-600',
                  isExecuting && 'cursor-not-allowed opacity-50',
                )}
              >
                {isExecuting ? (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin" />
                    <span>Executing...</span>
                  </>
                ) : (
                  <>
                    <Zap
                      size={14}
                      className="transition-colors group-hover:text-yellow-300"
                      fill="currentColor"
                    />
                    Execute All
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};
